/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import isEmail from 'validator/es/lib/isEmail';
import { FlexRow, FlexCol, TextField, Icon, useFlags } from 'sarsaparilla';
import { HolderInfoProps, HolderInfoDefaults, PassTypeProps } from '../props';

export default function HolderInfoForm({
    holderInfo = HolderInfoDefaults,
    isInternal = false,
    isFieldSales,
    submitAttempted,
    number = 1,
    isMandatory = true,
    onChange,
    showToolTip = false,
    passType,
}) {
    const { email: confirmation } = holderInfo;

    const [confirmationState, setConfirmationState] = useState(confirmation);

    const emailsMatch = (email, conf) => email && email === conf;

    const validateEmail = (value) => ({
        isValid: isEmail(value),
        formattedValue: null,
        errorText: 'Please provide a valid email address',
    });

    const isCommercialPass = passType?.features?.['is-commercial-pass'];

    const formIsValid = (holder = HolderInfoDefaults, conf = confirmationState) => {
        const { first_name: fn, last_name: ln, email, zip_code, company_name } = holder;

        const nameValid = isCommercialPass ? !!company_name : !!(fn && ln);
        const emailMatchCheck = isCommercialPass ? true : emailsMatch(email, conf);

        return !!(
            nameValid &&
            email &&
            zip_code &&
            validateEmail(email).isValid &&
            emailMatchCheck
        );
    };

    useEffect(() => {
        onChange(holderInfo, formIsValid(holderInfo));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFieldChange = (field, { target: { value } }) => {
        const holder = { ...holderInfo, [field]: value };
        onChange(holder, formIsValid(holder));
    };

    const handleFieldBlur = (field) => {
        const value = holderInfo[field];
        if (value && value !== value.trim()) {
            const holder = { ...holderInfo, [field]: value.trim() };
            onChange(holder, formIsValid(holder));
        }
    };

    const handleEmailConfirmationChange = ({ target: { value: conf } }) => {
        const holder = { ...holderInfo, email_confirmation: conf };
        onChange(holder, formIsValid(holderInfo, conf));
        setConfirmationState(conf);
    };

    const validateConfirmationEmail = (conf) => ({
        isValid: emailsMatch(holderInfo.email, conf),
        formattedValue: null,
        errorText: 'Email and confirmation do not match.',
    });

    return (
        <div>
            <FlexRow>
                {isCommercialPass ? (
                    <FlexCol xl="auto" className="rec-form-item-wrap">
                        <TextField
                            label="Company Name"
                            id={`holder-company-name-${number}`}
                            data-testid={`holder-company-name-${number}`}
                            value={holderInfo.company_name}
                            isRequired={isMandatory}
                            isDisabled={!!(isFieldSales && holderInfo.company_name)}
                            shouldValidateOnUpdate={submitAttempted}
                            onChange={(e) => handleFieldChange('company_name', e)}
                            onBlur={() => handleFieldBlur('company_name')}
                        />
                    </FlexCol>
                ) : (
                    <>
                        <FlexCol xl="auto" className="rec-form-item-wrap">
                            <TextField
                                label="First Name"
                                id={`holder-first-name-${number}`}
                                data-testid={`holder-first-name-${number}`}
                                value={holderInfo.first_name}
                                isRequired={isMandatory}
                                isDisabled={!!(isFieldSales && holderInfo.first_name)}
                                shouldValidateOnUpdate={submitAttempted}
                                onChange={(e) => handleFieldChange('first_name', e)}
                            />
                        </FlexCol>

                        <FlexCol xl="auto" className="rec-form-item-wrap">
                            <TextField
                                label="Last Name"
                                id={`holder-last-name-${number}`}
                                data-testid={`holder-last-name-${number}`}
                                value={holderInfo.last_name}
                                isRequired={isMandatory}
                                isDisabled={!!(isFieldSales && holderInfo.last_name)}
                                shouldValidateOnUpdate={submitAttempted}
                                onChange={(e) => handleFieldChange('last_name', e)}
                            />
                        </FlexCol>
                    </>
                )}
            </FlexRow>

            <FlexRow>
                <FlexCol xl="auto" className="rec-form-item-wrap">
                    <TextField
                        type="email"
                        label="Email Address"
                        id={`holder-email-${number}`}
                        value={holderInfo.email}
                        isRequired={isMandatory}
                        isDisabled={
                            !!(isFieldSales && holderInfo.email) || isCommercialPass
                        }
                        validator={validateEmail}
                        shouldValidateOnUpdate={submitAttempted}
                        onChange={(e) => handleFieldChange('email', e)}
                    />
                </FlexCol>

                {!isCommercialPass && (
                    <FlexCol
                        xl="auto"
                        className="parkpass-email-confirmation rec-form-item-wrap"
                    >
                        {!isInternal && (
                            <TextField
                                type="email"
                                label="Re-Enter Email Address"
                                id={`holder-email-confirmation-${number}`}
                                data-testid={`holder-email-confirmation-${number}`}
                                value={confirmationState}
                                isRequired={isMandatory}
                                isDisabled={!!(isFieldSales && holderInfo.email)}
                                validator={validateConfirmationEmail}
                                shouldValidateOnUpdate={submitAttempted}
                                onChange={handleEmailConfirmationChange}
                            />
                        )}

                        {!isInternal &&
                            emailsMatch(holderInfo.email, confirmationState) && (
                                <span className="parkpass-email-confirmed">
                                    <Icon iconName="check" />
                                </span>
                            )}
                    </FlexCol>
                )}
            </FlexRow>

            <FlexRow>
                <FlexCol xl={6} lg={6}>
                    <div className="rec-form-item-wrap">
                        <TextField
                            id={`holder-zip-code-${number}`}
                            value={holderInfo?.zip_code}
                            hintText={
                                showToolTip
                                    ? `Note: This annual pass is for U.S. Residents only. If you are a Non U.S. Resident, please navigate back to the Site Pass Selection page and select an alternative pass type.`
                                    : ''
                            }
                            label="Zip Code"
                            isRequired={isMandatory}
                            shouldValidateOnUpdate={submitAttempted}
                            onChange={(e) => handleFieldChange('zip_code', e)}
                        />
                    </div>
                </FlexCol>
            </FlexRow>
        </div>
    );
}

HolderInfoForm.propTypes = {
    holderInfo: HolderInfoProps,
    isInternal: PropTypes.bool,
    isFieldSales: PropTypes.bool,
    submitAttempted: PropTypes.bool,
    number: PropTypes.number,
    isMandatory: PropTypes.bool,
    onChange: PropTypes.func,
    showToolTip: PropTypes.bool,
    passType: PassTypeProps,
};

// cspell:ignore parkpass, testid
